import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Alert, TextLink } from '@kontentino/ui';
import SearchTextInput from 'app/components/SearchTextInput';
import { ApiClientError } from 'api/client';
import { NewAddedPage } from 'types/Page';
import { SocialLoginPage } from 'types/SocialLogin';
import useSocialProfilesSelection from 'components/modals/newSocialMediaProfile/automaticForm/useSocialProfilesSelection';
import { guideLinks } from 'components/modals/newSocialMediaProfile/errorStepsGuide/errorStepsGuide';
import type { PortfolioMarkers } from '../SelectSocialProfiles';
import type { SocialProfile } from '../SelectSocialProfileOption';
import {
  buildPortfolioColumns,
  firstOpenKey,
  resolveOpenKey,
} from './portfolioColumns';
import BulkSelectLinks from './BulkSelectLinks';
import PortfolioList from './PortfolioList';
import PortfolioPagesPanel from './PortfolioPagesPanel';
import PickerFooter from './PickerFooter';

type Props = {
  data: SocialLoginPage[];
  // Every portfolio of the poll: its markers, and the name of one that came
  // with no pages.
  portfolios: PortfolioMarkers[];
  isPartial: boolean;
  pageType: number;
  connectedPagesExternalIds: string[];
  // Reached from a Reconnect/Refresh entry: only the connected pages start
  // ticked. A plain add ticks every page that can be connected.
  isReconnect: boolean;
  // The one page a profile row's button was clicked for: only it starts
  // ticked, and its portfolio starts open.
  targetExternalId?: string | null;
  userKey?: string | null;
  clientBusinessId?: string | null;
  onPagesCreate: (page: NewAddedPage) => void;
  onError?: (error: ApiClientError) => void;
  // 'modal' fills a flex-column modal and sits side by side from `sm`.
  // 'page' fills a flex-column page (onboarding's 552px step) and is stacked
  // at every width: two columns do not fit in it.
  layout?: 'modal' | 'page';
  // Add's test id, for a host that already has one for its submit.
  submitDataCy?: string;
  // Add's analytics hook. The modal keeps the one-list picker's confirm
  // hook (the default); another host gives its own, or none (null), so its
  // confirms are not counted as the modal's.
  submitDataName?: string | null;
  // How long Add waits for social-login, in ms; onboarding waits less than
  // the modal.
  confirmTimeout?: number;
};

/**
 * The Safe Connect page picker: portfolios on the left, the open portfolio's
 * pages on the right.
 *
 * Scrolling: the picker is a flex column whose middle (the two columns) takes
 * what is left of its height. Each column scrolls on its own; the title,
 * description, partial warning, search and footer never scroll. It needs a
 * flex-column parent with a capped ('modal') or definite ('page') height.
 *
 * Mount it once the pages and the connected ids have loaded: the selection
 * hook reads its default ticks once, on mount.
 */
const TwoColumnPicker = ({
  data,
  portfolios,
  isPartial,
  pageType,
  connectedPagesExternalIds,
  isReconnect,
  targetExternalId,
  userKey,
  clientBusinessId,
  onPagesCreate,
  onError,
  layout = 'modal',
  submitDataCy,
  submitDataName,
  confirmTimeout,
}: Props) => {
  const { t } = useTranslation();
  const isPage = layout === 'page';
  // A reconnect ticks only the connected pages, so its description leaves out
  // "All pages are selected by default" (R29).
  const descriptionKey = isReconnect
    ? 'newSocialMediaProfile.twoColumn.descriptionReconnect'
    : 'newSocialMediaProfile.twoColumn.description';
  const descriptionKeyStacked = isReconnect
    ? 'newSocialMediaProfile.twoColumn.descriptionStackedReconnect'
    : 'newSocialMediaProfile.twoColumn.descriptionStacked';
  const selection = useSocialProfilesSelection({
    data,
    pageType,
    connectedPagesExternalIds,
    onPagesCreate,
    onError,
    clientBusinessId,
    tokenType: 'system_user_obo',
    userKey,
    defaultSelection: isReconnect ? 'connected' : 'allConnectable',
    targetExternalId,
    limitCheck: 'onSubmit',
    confirmTimeout,
  });
  const { profiles } = selection;

  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  // The portfolio the person opened; null until they open one.
  const [chosenKey, setChosenKey] = useState<string | null>(null);

  const allColumns = useMemo(
    () => buildPortfolioColumns(profiles, portfolios, ''),
    [profiles, portfolios],
  );
  const columns = useMemo(
    () =>
      needle ? buildPortfolioColumns(profiles, portfolios, needle) : allColumns,
    [allColumns, needle, portfolios, profiles],
  );
  // Before anything is opened, the target page's portfolio is, else the first
  // with a page to tick, and a search keeps it open while it lists it.
  const initialKey = useMemo(
    () =>
      allColumns.find((column) =>
        column.profiles.some((profile) => profile.id === targetExternalId),
      )?.key ?? firstOpenKey(allColumns),
    [allColumns, targetExternalId],
  );
  const openKey = resolveOpenKey(columns, chosenKey ?? initialKey);
  const openColumn = columns.find((column) => column.key === openKey) ?? null;

  const selectedIds = useMemo(
    () => new Set(selection.selectedProfiles),
    [selection.selectedProfiles],
  );
  // What the portfolios' own Select all and None act on: every page a search
  // leaves, so they never change a tick the person cannot see.
  const listedConnectableIds = columns.flatMap(
    (column) => column.connectableIds,
  );

  // Every page row gets the same handler, which calls the latest
  // `handleSelect`, so a memoised row re-renders only when its own state
  // changes. Updated after commit, as in the one-list picker.
  const handleSelectRef = useRef(selection.handleSelect);
  useLayoutEffect(() => {
    handleSelectRef.current = selection.handleSelect;
  });
  const handleRowClick = useCallback(
    (id: SocialProfile['id']) => handleSelectRef.current(String(id)),
    [],
  );
  const handleOpen = useCallback((key: string) => setChosenKey(key), []);

  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openRowRef = useRef<HTMLButtonElement>(null);
  const lastNeedle = useRef(needle);

  // Opening a portfolio shows its pages from the top; a search shows both
  // columns from the top. Either way, and on load, the open row is brought
  // into view in the left column (`nearest` leaves it be when visible).
  useLayoutEffect(() => {
    if (lastNeedle.current !== needle) {
      lastNeedle.current = needle;

      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    }

    if (panelRef.current) {
      panelRef.current.scrollTop = 0;
    }

    // Not every environment implements it (jsdom does not).
    openRowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [openKey, needle]);

  const guideLink = guideLinks[pageType as keyof typeof guideLinks] as
    | string
    | undefined;

  return (
    <div
      data-cy="two-column-picker"
      // Spacing is tighter below `sm`, so a short phone keeps room for the
      // lists between the fixed parts.
      className={clsx(
        'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-3 sm:tw-gap-4',
        // A page gives it its height; a tall screen still stops at the
        // modal's cap.
        isPage ? 'sm:tw-max-h-[800px]' : 'tw-pb-4 sm:tw-pb-6',
      )}
    >
      <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-1">
        <h2
          className={clsx(
            'tw-m-0 tw-text-grayscale-180',
            isPage
              ? // The onboarding steps' display font.
                'tw-font-tobias tw-text-xl tw-font-bold sm:tw-text-2xl'
              : // The app's modal titles (Modal.Header), clear of the close
                // button.
                'tw-pr-8 tw-text-lg tw-font-semibold',
          )}
        >
          {t('newSocialMediaProfile.twoColumn.title')}
        </h2>
        <p className="tw-m-0 tw-text-sm tw-text-grayscale-140 sm:tw-text-md">
          {/* Stacked, there is no left and right: always on a page, and in
              the modal below `sm`. */}
          {isPage ? (
            t(descriptionKeyStacked)
          ) : (
            <>
              <span className="sm:tw-hidden">{t(descriptionKeyStacked)}</span>
              <span className="max-sm:tw-hidden">{t(descriptionKey)}</span>
            </>
          )}
        </p>
      </div>

      {isPartial && (
        <Alert
          variant="warning"
          className="tw-shrink-0"
          data-cy="select-social-profiles_partial-warning"
        >
          {t('newSocialMediaProfile.partialPagesWarning')}
        </Alert>
      )}

      {/* The scroll region: side by side from `sm` in a modal. Stacked
          (below `sm`, and always on a page) everything is one column: the
          left wrapper dissolves (`contents`), so search, heading, list and
          pages are siblings and the space left is split 2:3 between the list
          and the pages. The list keeps a two-row floor; the pages give way
          first. */}
      <div
        className={clsx(
          'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-2',
          isPage ? 'sm:tw-gap-3' : 'sm:tw-flex-row sm:tw-gap-4',
        )}
        data-cy="two-column-picker_columns"
      >
        <div
          className={clsx(
            'tw-contents',
            !isPage &&
              'sm:tw-flex sm:tw-min-h-0 sm:tw-w-[320px] sm:tw-shrink-0 sm:tw-flex-col sm:tw-gap-3',
          )}
          data-cy="two-column-picker_left-column"
        >
          <div className="tw-shrink-0">
            <SearchTextInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
              data-cy="two-column-picker_search-input"
              data-name="select-social-profiles_search-input"
            />
          </div>
          <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-1">
            <span className="tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-grayscale-100">
              {t('newSocialMediaProfile.twoColumn.portfoliosHeading')}
            </span>
            {listedConnectableIds.length > 0 && (
              <BulkSelectLinks
                onSelectAll={() =>
                  selection.handleSelectAll(listedConnectableIds)
                }
                onSelectNone={() =>
                  selection.handleSelectNoneInGroup(listedConnectableIds)
                }
                scopeLabel={t(
                  'newSocialMediaProfile.twoColumn.portfoliosHeading',
                )}
                dataCyPrefix="two-column-picker_portfolios"
              />
            )}
          </div>
          <PortfolioList
            columns={columns}
            openKey={openKey}
            selectedIds={selectedIds}
            onOpen={handleOpen}
            listRef={listRef}
            openRowRef={openRowRef}
            className={
              isPage
                ? // Its 2/5 share, but never taller than its rows, so a
                  // short list leaves the room to the pages. The floor is two
                  // rows, or the one row there is. (In a page the share stays
                  // under the modal's 35vh cap by itself.)
                  clsx(
                    'tw-max-h-max tw-flex-[2_1_0%] tw-overflow-y-auto',
                    columns.length > 1 ? 'tw-min-h-[104px]' : 'tw-min-h-max',
                  )
                : 'tw-max-h-[35vh] tw-min-h-0 tw-overflow-y-auto max-sm:tw-min-h-[104px] max-sm:tw-flex-[2_1_0%] sm:tw-max-h-none sm:tw-flex-1'
            }
          />
        </div>
        <PortfolioPagesPanel
          column={openColumn}
          isSearching={needle.length > 0}
          selectedIds={selectedIds}
          onRowClick={handleRowClick}
          onSelectAll={() =>
            openColumn && selection.handleSelectGroup(openColumn.connectableIds)
          }
          onSelectNone={() =>
            openColumn &&
            selection.handleSelectNoneInGroup(openColumn.connectableIds)
          }
          panelRef={panelRef}
          className={clsx(
            'tw-min-h-0 tw-min-w-0 tw-overflow-y-auto',
            isPage ? 'tw-flex-[3_1_0%]' : 'tw-flex-1 max-sm:tw-flex-[3_1_0%]',
          )}
        />
      </div>

      {guideLink && (
        <p className="tw-m-0 tw-shrink-0 tw-text-sm tw-text-grayscale-140">
          {t('newSocialMediaProfile.twoColumn.setupGuide.question')}{' '}
          <TextLink>
            <a
              href={guideLink}
              target="_blank"
              rel="noreferrer"
              className="tw-font-semibold tw-underline"
              data-cy="two-column-picker_setup-guide-link"
            >
              {t('newSocialMediaProfile.twoColumn.setupGuide.link')}
            </a>
          </TextLink>
        </p>
      )}

      <PickerFooter
        selectedCount={selection.selectedCount}
        portfoliosCount={selection.selectedPortfolioIds.length}
        newSelectedCount={selection.newSelectedCount}
        remainingProfiles={selection.remainingProfiles}
        isOverLimit={selection.isOverLimit}
        isSubmitting={selection.isSubmitting}
        isReconnect={isReconnect}
        onSubmit={selection.handleSubmit}
        submitDataCy={submitDataCy}
        submitDataName={submitDataName}
      />
    </div>
  );
};

export default TwoColumnPicker;
